<script setup lang="ts">
	import { useBaseFile } from './BaseFile';
	import { useCrmFormFile } from './CrmFormFile';
	import FileBrowserModal from './FileBrowserModal.vue';
	import { CrmFormFile } from './CrmFormFile';
	import {ref, reactive, computed, onMounted, onUnmounted} from 'vue';
	import CloseCross from "@/fd/editors/file/CloseCross.vue";
	import {injectEnv} from "@fd/Environment";
	import SymIcon from "@fd/helpers/SymIcon.vue";

	const props = defineProps({
		showDrag: { type: Boolean, required: false, default: true },
		showDisc: { type: Boolean, required: false, default: true },
		showClip: { type: Boolean, required: false, default: true },
		showDoc: { type: Boolean, required: false, default: true },
		preview: { type: Boolean, required: false, default: true },
		bundleDrag: String,
		restApi: String,
		restParams: String,
		thumbnailSize: { type: Number, required: false, default: 50 },
		typeFilter: String,
		readOnly: Boolean,
		zoneClass: String,
		zoneStyle: String
	});
	
	const crmFormFiles = ref<CrmFormFile[]>([])
	
	const env = injectEnv();
	const fileInputId = ref(null);
	const bundleDrag=env.msg(props.bundleDrag?props.bundleDrag:"fileZone.drag.short");
	
	const emit = defineEmits(['uploaded']);
	
	const { CLASSEVENTS, dropClass, errorMessage, isError, 
			uploadingFile, uploading, uploadingFileDone,
			active, setActive, setInactive, 
			onDrop, onInputChange, onClipboard, onCrmFile,
			loadZone, removeFromZone } = useBaseFile(props, uploadedFile,uploadedAllFiles, emit);
	
	const { thumbnailUrl, limitFileName } = useCrmFormFile();
	
	
	function uploadedFile(data): void {
		//nothing
	}
	
	function uploadedAllFiles(): void {
		//loading newly uploaded files
		loadFileZone();
		emit('uploaded');
	}
	
	function loadFileZone() {
		if (props.preview) {
			console.log("loading filezone");
			loadZone().then((files)=> {
				crmFormFiles.value=files;
				console.log("crmFormFiles:"+JSON.stringify(crmFormFiles.value));
			});
		}
	}
	
	function removeFile(id: string) {
		console.log("Removing id:"+id);
		const index = crmFormFiles.value.findIndex((item)=>{return item.id===id;});
		if (index>=0) {
			removeFromZone(id)
				.then((result)=> {
						if (result) {
							crmFormFiles.value.splice(index, 1);
						}
				});	
		}
	}
	
	function onFileInput(): void {
		fileInputId.value.click();
	}

	function preventDefaults(e): void {
		e.preventDefault();
	}
		
	onMounted(() => {
		CLASSEVENTS.forEach((eventName) => {
			document.body.addEventListener(eventName, preventDefaults);
		});
		loadFileZone();
	});
	
	onUnmounted(() => {
		CLASSEVENTS.forEach((eventName) => {
			document.body.removeEventListener(eventName, preventDefaults);
		});
	});
</script>
<template>
		<div v-if="readOnly" class="fileZone" :class="zoneClass" :style="zoneStyle">
			<div class="fileZoneReadOnlyText">
				<sym-icon name="paper-clip"/>
				{{bundleDrag}}
			</div>
		</div>
		<div v-else @drop.prevent="onDrop" :data-active="active" 
			@dragenter.prevent="setActive" @dragover.prevent="setActive"
			@dragleave.prevent="setInactive" :class="[zoneClass,dropClass,'fileZone']" :style="zoneStyle">
			<div class="zoneContainer" v-show="preview && crmFormFiles.length>0">
				<div class="box-file" v-for="crmFormFile in crmFormFiles" :key="crmFormFile.id">
					<close-cross v-if="!readOnly" @click.prevent="removeFile(crmFormFile.id)" class="corner"></close-cross>
					<img v-if="crmFormFile.picture" :src="thumbnailUrl(crmFormFile, thumbnailSize)" :alt="crmFormFile.fileName" :title="crmFormFile.fileName"/>
					<span v-else class="fi bigIcon" v-html="crmFormFile.icon" :title="crmFormFile.fileName" ></span>
					<div class="box-file-name"><a :href="restApi+'/'+crmFormFile.id" target="_blank">{{limitFileName(crmFormFile)}}</a></div>
				</div>				
			</div>
			<template v-if="uploading">
				<div class="fileZoneText">
					<sym-icon v-show="!uploadingFileDone" class="uploadingIcon" name="refresh" />
					{{uploadingFile.uploadingTitle}}
					<sym-icon v-show="uploadingFileDone" class="hover-icon" name="problem-task" />
				</div>
			</template>
			<template v-else>
				<div v-if="active" class="fileZoneText">
					<span>{{env.msg("fileZone.drop")}}</span>
				</div>
				<template v-else >
					<div v-if="showDrag" class="fileZoneText" @click.prevent="onFileInput">
						<sym-icon name="paper-clip"/>
						<span>{{bundleDrag}}</span>
					</div>
					<div v-show="showDisc || showClip || showDoc" class="fileZoneButtons">
						<button v-if="showDisc" class="sbutton" @click.prevent="onFileInput">
							<sym-icon name="upload"/> <span>{{env.msg("fileZone.fromDisc")}}</span>
						</button>
						<button v-if="showClip" class="sbutton" @click.prevent="onClipboard">
							<sym-icon name="clipboard"/> <span>{{env.msg("fileZone.fromClipboard")}}</span>
						</button>
						<file-browser-modal v-if="showDoc" :button="true" @select-file="onCrmFile"/>
					</div>
				</template>
			</template>
			<input type="file" ref="fileInputId" multiple @change="onInputChange" style="display: none;" />
		</div>
		<div v-if="isError" class="errors">
			<span class="perr">{{errorMessage}}</span>
		</div>
</template>

<style>
.zoneContainer {
	display: flex;
	column-gap: 8px;
	flex-flow: row wrap;
	justify-content: space-evenly;
	align-items: center;
	padding: 8px;
}

.box-file {
	max-width: 200px;
	position:relative;
	text-align: center;
	overflow-wrap: break-word;
}

.box-file img {
	max-width:100%;
	max-height:100%;
}

.box-file div {
	max-width:100%;
}
.box-file-name {
	font-size: var(--font-size-s);
}

.bigIcon {
	font-size: 40px;
	padding: 10px;
}

.corner {
	position:absolute;
    top:5px;
    right:5px;
}
</style>
